import { useEffect, useState, useCallback, memo } from "react";
import { loadImage } from "../../utils/loadImage";
import SkeletonImage from "antd/es/skeleton/Image";

interface MediaContentProps {
  src: string;
  isImage?: boolean;
  isVideo?: boolean;
}

export const MediaContent = memo(function MediaContent(
  props: MediaContentProps
) {
  const { src } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [url, setUrl] = useState<string | undefined>(undefined);

  const isImage =
    src.toLowerCase().endsWith(".jpg") ||
    src.toLowerCase().endsWith(".jpeg") ||
    src.toLowerCase().endsWith(".png") ||
    src.toLowerCase().endsWith(".gif") ||
    src.toLowerCase().endsWith(".webp") ||
    src.toLowerCase().endsWith(".heic") ||
    src.toLowerCase().endsWith(".heif") ||
    (src.startsWith("blob:") && !props.isVideo) ||
    props.isImage;

  const isVideo =
    src.toLowerCase().endsWith(".mp4") ||
    src.toLowerCase().endsWith(".webm") ||
    src.toLowerCase().endsWith(".ogg") ||
    src.toLowerCase().endsWith(".mov") ||
    src.toLowerCase().endsWith(".avi") ||
    props.isVideo;

  const onLoaded = useCallback(() => {
    setIsLoading(false);
  }, []);

  const onError = useCallback(() => {
    setIsLoading(false);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadImageFn = async () => {
      try {
        const cachedUrl = await loadImage(src);
        if (isMounted) {
          setUrl(cachedUrl);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Failed to load image:", error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadImageFn();

    return () => {
      isMounted = false;
    };
  }, [src]);

  return (
    <div className="absolute top-0 left-0 w-auto h-auto">
      {isLoading && (
        <SkeletonImage
          active
          className="!w-full !h-full"
          rootClassName="!w-full !h-full"
        />
      )}
      {isImage && url && (
        <img
          alt="media_content"
          src={url}
          className="w-full h-full object-cover"
          onLoad={onLoaded}
          onError={onError}
        />
      )}
      {isVideo && url && (
        <video
          src={url}
          width="100%"
          height="100%"
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          onCanPlay={onLoaded}
          onError={onError}
        />
      )}
    </div>
  );
});
